import { DifficultyLevel } from '../interfaces/question.interface';
import { PowerRound } from '../interfaces/power-burst.interface';

interface Pow { base: number; exp: number; }

// Powers kept ≤ 64 so the "burst" can draw every orb.
const EASY: Pow[] = [
  { base: 2, exp: 2 }, { base: 3, exp: 2 }, { base: 4, exp: 2 }, { base: 5, exp: 2 }, { base: 6, exp: 2 }
];
const MEDIUM: Pow[] = [
  { base: 2, exp: 3 }, { base: 2, exp: 4 }, { base: 3, exp: 3 }, { base: 7, exp: 2 }
];
const HARD: Pow[] = [
  { base: 2, exp: 5 }, { base: 2, exp: 6 }, { base: 4, exp: 3 }, { base: 8, exp: 2 }
];

function randInt(lo: number, hi: number): number {
  return lo + Math.floor(Math.random() * (hi - lo + 1));
}
function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
const pick = <T>(a: T[]): T => a[Math.floor(Math.random() * a.length)];

/** Build four options: the value + three plausible distractors. The base×exp
 *  ("multiply instead of power") trap is always included when it differs. */
function makeOptions(base: number, exp: number, value: number): number[] {
  const cands = [
    base * exp,                       // the classic "multiplied" mistake
    Math.pow(exp, base),              // swapped base/exponent
    Math.pow(base, exp - 1),          // one power too few
    Math.pow(base, exp + 1),          // one power too many
    value + base, value - base
  ];
  const opts = new Set<number>([value]);
  if (base * exp !== value) opts.add(base * exp);
  for (const c of shuffle(cands)) {
    if (opts.size >= 4) break;
    if (c > 0 && Number.isInteger(c) && !opts.has(c)) opts.add(c);
  }
  while (opts.size < 4) opts.add(value + randInt(1, 9));
  return shuffle([...opts]);
}

function toRound(p: Pow, d: DifficultyLevel, idx: number): PowerRound {
  const value = Math.pow(p.base, p.exp);
  return { id: `pow-${idx}-${p.base}e${p.exp}`, base: p.base, exp: p.exp, value, options: makeOptions(p.base, p.exp, value), difficulty: d };
}

/** Build a session's rounds on an easy→hard ramp (squares → cubes → higher). */
export function buildPowerBurstRounds(count: number): PowerRound[] {
  const n = Math.max(1, count);
  const out: PowerRound[] = [];
  for (let i = 0; i < n; i++) {
    const t = n === 1 ? 0 : i / (n - 1);
    if (t < 0.34) out.push(toRound(pick(EASY), DifficultyLevel.EASY, i));
    else if (t < 0.68) out.push(toRound(pick(MEDIUM), DifficultyLevel.MEDIUM, i));
    else out.push(toRound(pick(HARD), DifficultyLevel.HARD, i));
  }
  return out;
}
